/**
 * O botão discreto da fila de triagem que roda o piloto com e-mail real, em
 * lotes de 50: coleta, triagem e leitura do lote, e promoção do que ficou
 * pronto. Aparece só quando a API liga as rotas `/demo/*` (`GET /demo`
 * responde) — em qualquer ambiente, sem flag de build.
 */

import Button from '@mui/material/Button';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { Aviso } from '../../../shared/ui/primitivas';
import { leituraDaCaixaLigada } from './api/demoDoPipeline';
import { useDemoDoPipeline, type EstadoDaDemo } from './useDemoDoPipeline';

function andamento(e: EstadoDaDemo, lote: number): string {
  switch (e.etapa) {
    case 'coletando':
      return `Buscando até ${String(lote)} e-mails novos na caixa real…`;
    case 'triando':
      return `${String(e.coletadas ?? 0)} e-mails novos neste lote. Separando em tickets…`;
    case 'lendo':
      return (
        `Lendo o lote: e-mail ${String(e.lidos + 1)} · ${String(e.recebimentos)} recebimento` +
        `${e.recebimentos === 1 ? '' : 's'} · agente em ${String(e.comAgente)}` +
        (e.falhasDeLeitura > 0 ? ` · ${String(e.falhasDeLeitura)} sem leitura` : '')
      );
    case 'promovendo':
      return `${String(e.recebimentos)} recebimentos em ${String(e.lidos)} e-mails lidos. Promovendo a Caso…`;
    case 'pronta':
      return (
        `Lote de ${String(e.coletadas ?? 0)} e-mail${e.coletadas === 1 ? '' : 's'}: ` +
        `${String(e.lidos)} lido${e.lidos === 1 ? '' : 's'}, ${String(e.recebimentos)} recebimento` +
        `${e.recebimentos === 1 ? '' : 's'}, agente em ${String(e.comAgente)}. ` +
        (e.promovidos === null
          ? ''
          : `${String(e.promovidos)} virou Caso${e.promovidos === 1 ? '' : 's'}. `) +
        (e.motivo ? `${e.motivo} ` : '') +
        (e.sobrouNaCaixa ? 'Ainda há e-mail novo na caixa: clique de novo para o próximo lote.' : '')
      ).trim();
    default:
      return '';
  }
}

export function FaixaDaDemo() {
  const clienteDeConsulta = useQueryClient();
  // Recarrega a fila a cada passo: o ticket novo aparece enquanto a leitura anda.
  const aoAvancar = useCallback(() => { void clienteDeConsulta.invalidateQueries(); }, [clienteDeConsulta]);
  const { estado, iniciar, lote } = useDemoDoPipeline({ aoAvancar });
  const ligada = useQuery({
    queryKey: ['demo', 'ligada'],
    queryFn: ({ signal }) => leituraDaCaixaLigada(signal),
    retry: false,
    staleTime: Infinity,
  });
  if (ligada.data !== true) return null;
  const rodando = estado.etapa === 'coletando' || estado.etapa === 'triando' || estado.etapa === 'lendo' ||
    estado.etapa === 'promovendo';

  return (
    <section
      aria-label="Rodar o piloto com e-mail real"
      className="mt-2 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground"
    >
      <Button
        variant="text"
        size="small"
        disabled={rodando}
        onClick={() => { void iniciar(); }}
        sx={{ px: 0.5, minWidth: 0, fontSize: 12, textTransform: 'none' }}
      >
        {rodando ? 'Rodando…' : `Buscar ${String(lote)} e-mails da caixa real`}
      </Button>
      {estado.etapa !== 'parada' && (
        <span role="status" aria-live="polite">
          {andamento(estado, lote)}
        </span>
      )}
      {estado.etapa === 'falhou' && (
        <div className="basis-full">
          <Aviso tone="warn">
            {estado.erro?.status === 404
              ? 'As rotas de demo não estão ligadas nesta API: suba com Parser__Url e Parser__BearerToken (ver .env.demo.example).'
              : `Parou: ${estado.motivo ?? 'falha inesperada'}.`}
            {estado.erro?.correlationId && ` correlation_id: ${estado.erro.correlationId}.`}
          </Aviso>
        </div>
      )}
    </section>
  );
}
